/**
 * Trainingstage auf dem Gerät: Wochenregel pflegen, einzelne Termine
 * verschieben oder absagen, die kommenden Termine mit rotierendem Plan-Tag.
 *
 * Die Regeln stehen in `@kraftwerk/shared` (`trainingOccurrences`,
 * `rotatePlanDays`, `occurrenceId`) — dieselben liest der Scheduler für
 * „Losgehen um …".
 */

import {
  addDays,
  assignPlanDays,
  isoDateInZone,
  newId,
  occurrenceId,
  occurrenceStart,
  trainingOccurrences,
} from '@kraftwerk/shared';
import type { IsoDate, TrainingOccurrence } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalTrainingDay,
  LocalTrainingSlot,
  LocalWorkoutSession,
} from './local.js';
import { activePlan, listDays } from './plans.js';
import { stamp, writeRow, writeRows } from './write.js';

export async function listSlots(database: KraftwerkDatabase = db): Promise<LocalTrainingSlot[]> {
  const rows = await database.training_slots.toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.weekday - b.weekday || a.time.localeCompare(b.time));
}

export async function saveSlot(
  slot: Pick<LocalTrainingSlot, 'weekday' | 'time' | 'durationMin'> & {
    id?: string;
    active?: boolean;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalTrainingSlot> {
  const existing = slot.id ? await database.training_slots.get(slot.id) : undefined;
  return writeRow(
    'training_slots',
    {
      ...existing,
      id: slot.id ?? newId(),
      weekday: slot.weekday,
      time: slot.time,
      durationMin: slot.durationMin,
      active: slot.active ?? existing?.active ?? true,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function deleteSlot(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.training_slots.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  // Verschobene und abgesagte Termine dieser Regel gleich mit — sie hängen an ihr.
  const exceptions = (await database.training_days.toArray()).filter(
    (row) => row.slotId === id && !row.deletedAt,
  );
  if (exceptions.length > 0) {
    await writeRows(
      exceptions.map((row) => ({
        table: 'training_days' as const,
        row: { ...row, updatedAt: now, deletedAt: now },
        kind: 'delete' as const,
      })),
      database,
    );
  }
  await writeRow(
    'training_slots',
    { ...existing, updatedAt: now, deletedAt: now },
    'delete',
    database,
  );
}

async function listTrainingDays(database: KraftwerkDatabase): Promise<LocalTrainingDay[]> {
  return (await database.training_days.toArray()).filter((row) => !row.deletedAt);
}

export interface UpcomingTraining extends TrainingOccurrence {
  /** Plan-Tag nach Rotation; bei erledigten der tatsächlich trainierte. */
  planDayId: string | null;
  planDayName: string | null;
  /** Einheit an diesem Tag — dann gilt der Termin als erledigt. */
  sessionId: string | null;
  /** Vorbei ohne Einheit (18.10.2026) — verbraucht keinen Plan-Tag. */
  missed: boolean;
}

/**
 * Termine von heute an für `days` Tage, mit Plan-Tag und Erledigt-Vermerk.
 *
 * Erledigt heißt: An dem Tag gibt es eine Einheit. Die Rotation beginnt nach
 * dem zuletzt trainierten Plan-Tag; erledigte Termine behalten ihren.
 */
export async function upcomingTraining(
  timeZone: string,
  days = 14,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
): Promise<UpcomingTraining[]> {
  const from = isoDateInZone(now, timeZone);
  const to = addDays(from as IsoDate, days - 1);
  const [slots, rows, sessions, plan] = await Promise.all([
    listSlots(database),
    listTrainingDays(database),
    database.workout_sessions.toArray(),
    activePlan(database),
  ]);
  const planDays = plan ? await listDays(plan.id, database) : [];
  const nameOf = new Map(planDays.map((day) => [day.id, day.name]));

  const live = sessions
    .filter((session) => !session.deletedAt)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const sessionOn = new Map<string, LocalWorkoutSession>();
  for (const session of live) {
    const day = isoDateInZone(new Date(session.startedAt), timeZone);
    if (!sessionOn.has(day)) sessionOn.set(day, session);
  }
  const lastPlanDayId = live.find((session) => session.planDayId)?.planDayId ?? null;

  const occurrences = trainingOccurrences(slots, rows, from, to, timeZone);
  const assigned = assignPlanDays(occurrences, {
    planDayIds: planDays.map((day) => day.id),
    lastPlanDayId,
    trainedDates: new Set(sessionOn.keys()),
    now,
    timeZone,
  });

  return assigned.map(({ done, ...entry }) => {
    const session = done ? (sessionOn.get(entry.date) ?? null) : null;
    const planDayId = session ? session.planDayId : entry.planDayId;
    return {
      ...entry,
      planDayId,
      planDayName: planDayId ? (nameOf.get(planDayId) ?? null) : null,
      sessionId: session?.id ?? null,
    };
  });
}

export interface MissedTraining extends TrainingOccurrence {
  /** Der Plan-Tag, der jetzt aufs nächste Training wartet. */
  waitingPlanDayName: string | null;
}

/**
 * Verpasste Termine (18.10.2026), auf die noch eine Antwort fehlt: nachholen
 * (verschieben) oder auslassen (absagen). Nur die der letzten sieben Tage,
 * nur nach der letzten Einheit — wer danach trainiert hat, ist weiter — und
 * nur, was nach dem Anlegen bzw. Ändern seiner Regel lag: Eine neue
 * Wochenregel macht die vergangene Woche nicht rückwirkend zu verpassten
 * Terminen.
 */
export async function missedTraining(
  timeZone: string,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
): Promise<MissedTraining[]> {
  const today = isoDateInZone(now, timeZone);
  const from = addDays(today as IsoDate, -6);
  const [slots, rows, sessions, upcoming] = await Promise.all([
    listSlots(database),
    listTrainingDays(database),
    database.workout_sessions.toArray(),
    upcomingTraining(timeZone, 14, database, now),
  ]);
  const lastSession = sessions
    .filter((session) => !session.deletedAt)
    .map((session) => session.startedAt)
    .sort()
    .at(-1);
  const trainedDates = new Set(
    sessions
      .filter((session) => !session.deletedAt)
      .map((session) => isoDateInZone(new Date(session.startedAt), timeZone)),
  );
  // Untergrenze ist die Wochenregel — eine Terminzeile ändert sich auch beim
  // Antippen der Erinnerung, das macht den Termin nicht neu.
  const changedAt = new Map(slots.map((slot) => [slot.id, slot.updatedAt]));
  const waiting =
    upcoming.find((entry) => entry.status === 'planned' && !entry.missed && !entry.sessionId)
      ?.planDayName ?? null;

  return assignPlanDays(trainingOccurrences(slots, rows, from, today, timeZone), {
    planDayIds: [],
    lastPlanDayId: null,
    trainedDates,
    now,
    timeZone,
  })
    .filter((entry) => {
      if (!entry.missed) return false;
      const start = occurrenceStart(entry, timeZone).toISOString();
      if (lastSession && lastSession > start) return false;
      const since = entry.slotId ? changedAt.get(entry.slotId) : undefined;
      return !since || since < start;
    })
    .map(({ planDayId: _planDayId, missed: _missed, done: _done, ...entry }) => ({
      ...entry,
      waitingPlanDayName: waiting,
    }));
}

/** Die Zeile zu einem Termin — vorhanden, oder mit fester Kennung neu. */
async function rowFor(
  occurrence: TrainingOccurrence,
  database: KraftwerkDatabase,
): Promise<LocalTrainingDay> {
  const id =
    occurrence.dayId ??
    (occurrence.slotId && occurrence.originDate
      ? occurrenceId(occurrence.slotId, occurrence.originDate)
      : newId());
  const existing = await database.training_days.get(id);
  return (
    existing ?? {
      id,
      slotId: occurrence.slotId,
      originDate: occurrence.originDate,
      date: occurrence.date,
      time: occurrence.time,
      durationMin: occurrence.durationMin,
      status: 'planned',
      acknowledgedAt: null,
      updatedAt: stamp(),
      deletedAt: null,
    }
  );
}

export async function cancelOccurrence(
  occurrence: TrainingOccurrence,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await rowFor(occurrence, database);
  await writeRow(
    'training_days',
    { ...row, status: 'cancelled', updatedAt: stamp(), deletedAt: null },
    'upsert',
    database,
  );
}

export async function restoreOccurrence(
  occurrence: TrainingOccurrence,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await rowFor(occurrence, database);
  await writeRow(
    'training_days',
    { ...row, status: 'planned', updatedAt: stamp(), deletedAt: null },
    'upsert',
    database,
  );
}

export async function moveOccurrence(
  occurrence: TrainingOccurrence,
  date: string,
  time: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await rowFor(occurrence, database);
  await writeRow(
    'training_days',
    {
      ...row,
      date,
      time,
      status: 'planned',
      // Neuer Zeitpunkt, neue Erinnerung — eine alte Quittung gilt nicht mehr.
      acknowledgedAt: row.date === date && row.time === time ? row.acknowledgedAt : null,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

/** Zusätzlicher Termin ohne Wochenregel. */
export async function addExtraDay(
  input: { date: string; time: string; durationMin: number },
  database: KraftwerkDatabase = db,
): Promise<LocalTrainingDay> {
  return writeRow(
    'training_days',
    {
      id: newId(),
      slotId: null,
      originDate: null,
      date: input.date,
      time: input.time,
      durationMin: input.durationMin,
      status: 'planned',
      acknowledgedAt: null,
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

/** Zusätzlichen Termin entfernen. Termine der Regel werden abgesagt, nicht gelöscht. */
export async function removeExtraDay(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const existing = await database.training_days.get(id);
  if (!existing || existing.deletedAt || existing.slotId) return;
  const now = stamp();
  await writeRow(
    'training_days',
    { ...existing, updatedAt: now, deletedAt: now },
    'delete',
    database,
  );
}

/** Mitteilung „Losgehen" angetippt — schließt die Erinnerungskette beim nächsten Abgleich. */
export async function acknowledgeTrainingDay(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.training_days.get(id);
  if (!existing || existing.deletedAt || existing.acknowledgedAt) return;
  await writeRow(
    'training_days',
    { ...existing, acknowledgedAt: stamp(), updatedAt: stamp() },
    'upsert',
    database,
  );
}
